import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [clips, setClips] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [renders, setRenders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Project CRUD
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/projects');
      setProjects(res.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  const createProject = useCallback(async (projectData) => {
    const res = await api().post('/projects', projectData);
    setProjects(prev => [...prev, res.data]);
    setCurrentProject(res.data);
    return res.data;
  }, [api]);

  const updateProject = useCallback(async (projectId, updateData) => {
    const res = await api().put(`/projects/${projectId}`, updateData);
    setProjects(prev => prev.map(p => p.id === projectId ? res.data : p));
    if (currentProject?.id === projectId) {
      setCurrentProject(res.data);
    }
    return res.data;
  }, [api, currentProject]);

  const deleteProject = useCallback(async (projectId) => {
    await api().delete(`/projects/${projectId}`);
    setProjects(prev => prev.filter(p => p.id !== projectId));
    if (currentProject?.id === projectId) {
      setCurrentProject(null);
    }
  }, [api, currentProject]);

  const loadProject = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const [projectRes, clipsRes] = await Promise.all([
        api().get(`/projects/${projectId}`),
        api().get(`/clips/${projectId}`)
      ]);
      setCurrentProject(projectRes.data);
      setClips(clipsRes.data);
      
      // Try to load timeline
      try {
        const timelineRes = await api().get(`/timelines/${projectId}`);
        setTimeline(timelineRes.data);
      } catch {
        setTimeline(null);
      }
      
      // Load renders
      try {
        const rendersRes = await api().get(`/renders/${projectId}`);
        setRenders(rendersRes.data);
      } catch {
        setRenders([]);
      }
      
      return projectRes.data;
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Clip operations
  const addClip = useCallback(async (clipData) => {
    const res = await api().post('/clips', clipData);
    setClips(prev => [...prev, res.data]);
    return res.data;
  }, [api]);

  const updateClip = useCallback(async (clipId, updateData) => {
    const res = await api().put(`/clips/${clipId}`, updateData);
    setClips(prev => prev.map(c => c.id === clipId ? res.data : c));
    return res.data;
  }, [api]);

  const deleteClip = useCallback(async (clipId) => {
    await api().delete(`/clips/${clipId}`);
    setClips(prev => prev.filter(c => c.id !== clipId));
  }, [api]);

  // File uploads with progress tracking
  const uploadAudio = useCallback(async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api().post('/upload/audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });
    return res.data;
  }, [api]);

  const uploadVideo = useCallback(async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api().post('/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });
    return res.data;
  }, [api]);

  const uploadLogo = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api().post('/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }, [api]);

  // Timeline operations
  const createTimeline = useCallback(async (timelineData) => {
    const res = await api().post('/timelines', timelineData);
    setTimeline(res.data);
    return res.data;
  }, [api]);

  const updateTimeline = useCallback(async (projectId, updateData) => {
    const res = await api().put(`/timelines/${projectId}`, updateData);
    setTimeline(res.data);
    return res.data;
  }, [api]);

  // AI Analysis
  const analyzeLyrics = useCallback(async (lyrics, audioDuration) => {
    const res = await api().post('/ai/analyze-lyrics', { lyrics, audio_duration: audioDuration });
    return res.data;
  }, [api]);

  // Render operations
  const createRender = useCallback(async (renderData) => {
    const res = await api().post('/renders', renderData);
    setRenders(prev => [...prev, res.data]);
    return res.data;
  }, [api]);

  const fetchRender = useCallback(async (renderId) => {
    const res = await api().get(`/renders/detail/${renderId}`);
    setRenders(prev => prev.map(r => r.id === renderId ? res.data : r));
    return res.data;
  }, [api]);

  return (
    <ProjectContext.Provider value={{
      projects,
      currentProject,
      clips,
      timeline,
      renders,
      loading,
      fetchProjects,
      createProject,
      updateProject,
      deleteProject,
      loadProject,
      setCurrentProject,
      addClip,
      updateClip,
      deleteClip,
      uploadAudio,
      uploadVideo,
      uploadLogo,
      createTimeline,
      updateTimeline,
      analyzeLyrics,
      createRender,
      fetchRender,
      setClips,
      setTimeline
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
